// src/components/onboarding/onboardingCopy.js
//
// Every string the SP3 onboarding screens show (spec decision 14): the Welcome,
// Account and Verify screens, the four documents pages, the Documents list
// section and Settings › Consents. Screens read strings by key; tests look them
// up here instead of repeating literals. The ONE test that repeats the
// literals is tests/components/onboarding-copy.test.js, which pins these
// values to the spec's § Copy.
//
// The VALUES are [flow-dependent] (pending Astra's approval of the flows); the
// keys and the screens that read them are not. A wording change is one edit
// here.
//
// Not here, because they are catalog data the server serves (decision 13):
// document names, the esign tick label and sub-label (`esignWording`), every
// consent row's label / why / shortLabel.
//
// `·` is U+00B7, `›` is U+203A; apostrophes are straight (U+0027) as the spec
// prints them.

import { countWord } from "../intake/intakeCopy";

// ---- The header -------------------------------------------------------------
export const BRAND = "Astra STARs";

// ---- The three-phase rail ---------------------------------------------------
export const RAIL_DOCUMENTS = "Documents";
export const RAIL_ABOUT = "About you";
export const RAIL_PLAN = "Your plan";
export const RAIL_STEP = (index, count) => `Documents · ${index} of ${count}`;
export const RAIL_DONE = "Documents · done";
export const DOCUMENTS_TITLE = "Your documents";

// ---- Login link ------------------------------------------------------------
export const WELCOME_START_TRIAL = "Start your free trial";

// ---- Account (/signup) --------------------------------------------------------
export const ACCOUNT_TITLE = "Create account";
export const ACCOUNT_H1 = "Create your account";
export const ACCOUNT_INTRO =
  "Takes a minute. We text you a code to confirm your phone.";
export const ACCOUNT_FIRST_NAME = "First name";
export const ACCOUNT_LAST_NAME = "Last name";
export const ACCOUNT_EMAIL = "Email";
export const ACCOUNT_MOBILE = "Mobile";
export const ACCOUNT_PASSWORD = "Password";
export const ACCOUNT_PASSWORD_PLACEHOLDER = "At least 10 characters";
// Kept by owner decision (2026-09-26); not in the comp.
export const ACCOUNT_CONFIRM_PASSWORD = "Confirm password";
export const ACCOUNT_STATE = "State of residence";
export const ACCOUNT_ELIGIBILITY =
  "Astra STARs is open to U.S. residents, 18 and older.";
// "How Astra uses your information: Privacy Notice." with the link on
// "Privacy Notice": lead + link + ".".
export const ACCOUNT_PRIVACY_LEAD = "How Astra uses your information: ";
export const ACCOUNT_PRIVACY_LINK = "Privacy Notice";
export const ACCOUNT_VERIFIED_FIRST =
  "Email and phone are verified before anything else happens.";
export const ACCOUNT_CONTINUE = "Continue";
export const ACCOUNT_NAME_REQUIRED =
  "Enter your first and last name to continue.";
export const ACCOUNT_SIDE_TITLE = "Create your account";
export const ACCOUNT_SIDE_NEXT =
  "Next: three documents to acknowledge, then the agreement to read and sign.";

// ---- Verify (/verify-code) ----------------------------------------------------
export const VERIFY_TITLE = "Verify your phone";
export const VERIFY_H1 = "Enter the code we texted you.";
export const VERIFY_SENT_TO = (number) => `Sent to ${number}.`;
// "The code works for five minutes. Wrong number? Go back and change it." with
// the link on "Go back and change it": lead + link + ".".
export const VERIFY_EXPIRY_LEAD =
  "The code works for five minutes. Wrong number? ";
export const VERIFY_GO_BACK = "Go back and change it";
export const VERIFY_HELPER = "Enter all six digits to continue.";
export const VERIFY_CONTINUE = "Continue";
export const VERIFY_RESEND = "Resend code";
export const VERIFY_RESENT = "Code sent again";
export const VERIFY_COOLDOWN = "You can ask for another code in 60 seconds.";
export const VERIFY_RESEND_TOAST = "A new code is on its way.";
export const VERIFY_SEND_FAILED =
  "We couldn't send the code just now. Try again in a moment.";
export const VERIFY_SIDE_TITLE = "Verify your phone";
export const VERIFY_SIDE_NEXT =
  "Next: three documents to acknowledge, then the agreement to read and sign.";

// ---- Acknowledge (/onboarding/acknowledge) -------------------------------------
export const ACK_TITLE = "Your documents";
export const ACK_H1 = "Three documents to keep";
export const ACK_INTRO =
  "Nothing to sign here yet. Astra has to put these in your hands before you sign, and you can open any of them now or any time from your documents.";
export const ACK_SUPPLEMENTS_HEADING =
  "Your advisors' brochure supplements (Form ADV Part 2B)";
export const ACK_TICK_BROCHURE =
  "I've received and can read this, including my advisors' supplements.";
export const ACK_TICK = "I've received and can read this.";
export const ACK_HELPER = "Tick each one to continue.";
export const ACK_CONTINUE = "Acknowledge and continue";
export const ACK_SIDE_TITLE = "Acknowledge";
export const ACK_ACKNOWLEDGED = (date) => `Acknowledged ${date}`;
export const ACK_PAGES = (n) => `${n} pages`;
export const ACK_LOAD_FAILED = "We couldn't load this document. Try again.";
export const ACK_NOT_READY =
  "One of your documents isn't ready yet. We've told the team.";

// ---- Agreement (/onboarding/agreement) -----------------------------------------
export const AGREEMENT_TITLE = "Your documents";
export const AGREEMENT_H1 = "Before we can talk about your money";
export const AGREEMENT_INTRO_1 =
  "Astra is a registered investment adviser, so a few things have to be in writing before anyone here talks about your situation.";
export const AGREEMENT_INTRO_2 =
  "There's one thing to sign, the agreement below. Read it to the end, type your name, and continue. After that, a short page of choices about how we work with you. Nothing is charged today.";
export const AGREEMENT_DOC_TITLE = "Financial Consulting Agreement";
export const AGREEMENT_DOC_META = (versionLabel, pages) =>
  pages ? `${versionLabel} · ${pages} pages` : versionLabel;
export const AGREEMENT_END_MARKER = "End of the agreement";
export const AGREEMENT_UNLOCKED =
  "You've reached the end of the agreement. You can sign now.";
export const AGREEMENT_NAME_LABEL = "Type your name to sign";
export const AGREEMENT_SIGN_NOTE =
  "Typing your name signs electronically. We keep the version you read, the time, and the device you signed from, and put a signed copy in your documents.";
export const AGREEMENT_CANCEL_WINDOW = (date) =>
  `You can cancel at no cost for five business days from today, ${date}. Nothing is charged today.`;
export const AGREEMENT_HELPER_READ =
  "Read to the end of the agreement, then you can sign.";
export const AGREEMENT_HELPER_TICK =
  "Tick the box above to agree to sign electronically.";
export const AGREEMENT_HELPER_NAME = "Type your name to sign.";
export const AGREEMENT_SIGN = "Sign and continue";
export const AGREEMENT_DOWNLOAD = "Download a copy";
export const AGREEMENT_DOWNLOAD_TOAST =
  "A PDF of this version downloads. It does not unlock signing.";
export const AGREEMENT_SIDE_TITLE = "Sign the agreement";
export const AGREEMENT_SIDE_NEXT =
  "Next: a short page of choices about how we work with you.";
export const AGREEMENT_SIGNED = ({ date, time, versionLabel, typedName }) =>
  `Signed ${date}, ${time} · version ${versionLabel} · signed as '${typedName}'`;
export const AGREEMENT_REVISION_CHANGED =
  "This agreement was updated while you were away, so here it is again.";
export const AGREEMENT_NAME_MISMATCH =
  "That's different from the name on your account. Fine if that's how you sign.";

// ---- Resume ---------------------------------------------------------------------
export const RESUME = "Picking up where you left off.";

// ---- Consents (/onboarding/consents) --------------------------------------------
export const CONSENTS_TITLE = "Your documents";
export const CONSENTS_H1 = "How we work with you";
export const CONSENTS_INTRO =
  "One of these is required. The rest are yours to choose, and you can change them later under You › Settings.";
export const CONSENTS_REQUIRED_MARK = "(required)";
export const CONSENTS_WHY = "Why";
// Keyed by consent kind: data in the copy module, not a kind named in code.
export const CONSENTS_REQUIRED_HELPER = {
  recording: "The recording consent is required to continue.",
};
export const CONSENTS_REQUIRED_TOAST = {
  recording: "Meeting recording is required to continue.",
};
// A required kind published later with no keyed line above (decision 13).
export const CONSENTS_REQUIRED_GENERIC = (shortLabel) =>
  `${shortLabel} is required to continue.`;
export const CONSENTS_HELPER_DONE =
  "The rest can wait, and you can change them any time.";
export const CONSENTS_CONTINUE = "Continue";
export const CONSENTS_SIDE_TITLE = "Your choices";

// ---- Receipt (/onboarding/done) --------------------------------------------------
export const RECEIPT_TITLE = "Your documents";
export const RECEIPT_H1 = "That's the paperwork done";
export const RECEIPT_INTRO =
  "Signed, dated and saved. This list lives under You › Documents, and anything new lands there too.";
export const RECEIPT_DOCS_HEADING = "Agreements and disclosures";
export const RECEIPT_CONSENTS_HEADING = "Consents you gave";
export const RECEIPT_ACKNOWLEDGED = (date, edition) =>
  edition ? `Acknowledged ${date} · ${edition}` : `Acknowledged ${date}`;
export const RECEIPT_NOT_YET = "Not yet";
export const RECEIPT_CONTINUE = "Continue to your questions";
export const RECEIPT_SEE_DOCUMENTS = "See my documents";
export const RECEIPT_SIDE_TITLE = "Next";
// R142: the comp's sentence; the count is GET /onboarding/stage's
// intakePageCount (SP4), never written here. "pages" vs "questions" is Astra's.
export const RECEIPT_NEXT = (count) =>
  `${Number.isInteger(count) && count > 0 ? `${countWord(count)} short pages` : "Short pages"} about you and your money, so the first advisor who replies already knows where to start.`;
export const PILL_SIGNED = "Signed";
export const PILL_DELIVERED = "Delivered";

// ---- Documents list (/documents) and Settings › Consents (/profile) ---------------
export const DOCS_SECTION = "Agreements and disclosures";
export const DOCS_PREPARING = "Your copy is being prepared";
export const SETTINGS_CONSENTS = "Consents";
export const SETTINGS_WITHDRAW =
  "To withdraw, ask your advisors in your thread.";

// ---- House lines (SP1/SP2 voice, R71; spec § Error handling) -----------------------
// A page shown read-only (the client has passed it) moves on with this; it
// writes nothing.
export const READ_ONLY_CONTINUE = "Continue";
export const LOAD_FAILED = "Couldn't load. Try again.";
export const SAVE_FAILED = "Couldn't save. Try again.";
export const TRY_AGAIN = "Try again";
export const GENERIC_ERROR = "Something went wrong. Try again.";
